import React from 'react';
import Dock from 'react-dock';
import { Value } from 'slate'
import { connect} from 'react-redux';
import { withRouter } from 'react-router-dom';
import { css } from 'emotion';

import { keepDraftState } from 'shared/actions/draft';
import { noop, viewportWidth } from 'shared/helpers';
import { BASE_COLOR } from 'shared/constants/colors';
import {
  IPHONE_X_LOGICAL_WIDTH,
  NAVBAR_PORTAL_ROOT_ID,
  DOCK_TRANSITION_DURATION,
} from 'shared/constants/web';
import {
  NODE_TYPE as NT,
  MARK_TYPE as MT,
  RENDER_MODE as RM,
  HEADLINE_CHAR_LIMIT,
} from 'shared/constants/slate';
import { classes as cn } from 'shared/styles';
import {
  compose,
  formatModuleId,
  keycodeFromEvent,
  debounce,
} from 'shared/helpers';

import { cx } from 'helpers';
import {
  MediumRareEditor,
  Placeholder,
  nodes,
  plugins,
  utils,
} from 'components/slate';
import { Portal, Popover, popWithMotion, Menu } from 'components/elems';
import {
  BottomRightCornerFrame,
  // UserGuideBtn,
  HelpMenuBtn,
  EventListener,
} from 'components/misc';
import { StageContentBtn, StageContentPanel } from '../common';
import BlogUserGuidePanel from './BlogUserGuidePanel';
import BlogExportPreview from './BlogExportPreview';


const { HeadlineSwitch, renderDefaultNode } = nodes;

const {
  hotkeyMatcher,
  SetHotkey_ToggleMark,
  EditCode,
    editCodeOpts,
  EditList,
    editListOpts,
  AutoReplace,
  AutoReplaceRules,
  TrimSelection,
  TrimDocumentOnBlur,
  PatchUndo,
} = plugins;


const COMPOSER_INNER_MAX_WIDTH = 680;  // px
// Don't cover the entire screen in mobile
const SIDE_DOCK_WIDTH = IPHONE_X_LOGICAL_WIDTH - 32;  // px

const { cmd_b, cmd_i, cmd_opt_s, cmd_z } = hotkeyMatcher;

// Slate still has some quirky behaviors, so likely
// temporary overrides to get around them.
const overrideSlatePlugins = [
  TrimSelection(),
  PatchUndo({ matcher: cmd_z }),

  // Too hacky..?
  // Relies on the normalization rule applied to headline that
  // prevents whitespace at start. Headline is required for blog
  // module so we want to help users not splitting into body of
  // doc without the headline.
  {
    onKeyDown: (e, change) => {
      if (e.key !== 'Enter') return;

      const { startBlock, document: doc } = change.value;
      if (startBlock.type === NT.headline && doc.isEmpty) {
        return change.insertText('\n');
      }
    },
  }
];


const {
  toHeading,
  toBlockquote,
  toCodeMark,
  MakeCodeBlockRule,
  MakeBulletedListRule,
  MakeNumberedListRule,
} = AutoReplaceRules;

const editCodePlugin = EditCode(editCodeOpts);
const toCodeBlock = MakeCodeBlockRule(editCodePlugin);

const editListPlugin = EditList(editListOpts);
const toBulletedList = MakeBulletedListRule(editListPlugin);
const toNumberedList = MakeNumberedListRule(editListPlugin);

const autoReplacePlugins = [
  AutoReplace(toHeading),
  AutoReplace(toBlockquote),
  AutoReplace(toCodeBlock), // Before toCodeMark!
  AutoReplace(toCodeMark),
  AutoReplace(toBulletedList),
  AutoReplace(toNumberedList),
];


// NOTE(tyu): Remember SetHotkey_ToggleMark uses `toggleMarkWorkaround`
// util func to handle adding or removing marks which has
// special treatment around headline (hence, 'workaround').
// Certain validation rules, like `MakeRuleMustNotHaveMarks` may
// be working in conjunction with this in mind.
const hotkeyPlugins = [
  SetHotkey_ToggleMark({ matcher: cmd_b, mark: MT.bold }),
  SetHotkey_ToggleMark({ matcher: cmd_i, mark: MT.italic }),
  SetHotkey_ToggleMark({ matcher: cmd_opt_s, mark: MT.strikethrough }),
];


const DOCK = {
  stageContent: 'stageContent',
  userGuide: 'userGuide',
};


/*
 * BlogComposer
 */

const schema = {
  document: {
    nodes: [
      {
        types: [ NT.headline ],
        min: 1,
        max: 1,
      },
      {
        types: [
          NT.paragraph,
          NT.heading,
          NT.codeBlock,
          NT.ul,
          NT.ol,
          NT.blockquote,
          NT.image,
          NT.embed,
        ],
      },
    ],

    // Refer to PatchUndo for more context about Slate bug(?)
    normalize: (change, reason, context) => {
      const { child } = context;

      // TODO: Use SchemaViolations.ChildTypeInvalid after upgrading slate.
      // Also don't return here.. otherwise slate complains

      if (reason === 'child_required') {
        // There should be at least one headline, blank of not.
        const { document: doc } = change.value;

        change
          .insertNodeByKey(
            doc.key, 0, { kind: 'block', type: 'headline' }
          )
          .focus();

      } else if (reason === 'child_type_invalid' && child.type === NT.headline) {
        // If we somehow end up with more than one headline,
        // then probably the safest thing is to make it a paragraph.
        change.setNodeByKey(child.key, { type: NT.paragraph });

      } else if (reason === 'child_type_invalid' && child.type === NT.paragraph) {
        // Deleting into headline or something like that..
        change.setNodeByKey(child.key, { type: NT.headline });

      } else if (reason === 'child_type_invalid') {
        // Otherwise probably missing headline..?
        // TODO: Maybe insert headline at the start of doc..?
      }

    },
  },

  blocks: {
    [NT.headline]: {
      // Should be top level always w/o parent.
      parent: { types: [undefined] },
      nodes: [
        {
          kinds: ['text'],
          min: 1,
          max: 1,
        }
      ],
      normalize: (change, reason, context) => {
        // TODO: Use SchemaViolations.ChildTypeInvalid after upgrading slate.
        if (reason === 'parent_type_invalid') {
          change.setNodeByKey(context.node.key, { type: NT.paragraph });
        }
      },
    },

    [NT.heading]: {
      nodes: [
        { kinds: ['text'] }
      ],
      data: {
        // Let it start from h2
        level: v => Number.isInteger(v) && v > 1,
      },
      normalize: (change, reason, context) => {
        if (reason === 'node_data_invalid') {
          // No idea why but, when copy pasting text into header,
          // data level info gets wiped too. Fallback to default.
          // Maybe will be fixed in slate in the future?
          const nodeProps = utils.makeNodeProps({ type: NT.heading });
          change.setNodeByKey(context.node.key, nodeProps);
        }
      },
    },

    [NT.paragraph]: {
      nodes: [
        { kinds: ['text'] }
      ],
    },

    [NT.image]: {
      // isVoid is not necessarily always `false`, because
      // while isReceiving we are taking text input.

      // Should be top level always w/o parent
      parent: { types: [undefined] },

      normalize: (change, reason, context) => {
        if (reason === 'parent_type_invalid') {
          const { node, parent } = context;
          const { document: doc } = change.value;

          // Rescue from invalid parent, and place it below.
          const block = doc.getFurthestBlock(parent.key) || parent;
          const index = doc.nodes.findIndex(n => n.key === block.key);

          change.moveNodeByKey(node.key, doc.key, index+1);
        }
      },
    },

    [NT.embed]: {
      // isVoid is not necessarily always `false`, similar to image

      // Should be top level always w/o parent
      parent: { types: [undefined] },
    },
  },

};

const blankDocObj = {
  document: {
    nodes: [
      {
        kind: 'block',
        type: NT.headline,
        nodes: [
          // I guess there must be at least one node even if
          // empty for autoFocus to work!
          { kind: 'text', leaves: [] }
        ]
      },
    ],
  },
};




class BlogComposer extends React.Component {
  constructor(props) {
    super(props);
    const { draftState } = props;

    this.state = {
      blogValue: Value.fromJSON(draftState
        ? draftState
        : blankDocObj
      ),

      dockDisplayContext: null,
    };

    this.debounceKeepDraftState = debounce(function() {
      props.keepDraftState({
        moduleId: props.moduleId,
        draftState: this.state.blogValue,
      });
    }, 500).bind(this);

    this.stageContent = this.stageContent.bind(this);
  }

  componentWillMount() {
    // Most likely coming back from oauth callback,
    // try to restore state before it.
    const { state = {} } = this.props.location || {};
    const { isStagingContent = false } = state;

    const { document: doc } = this.state.blogValue;

    if (isStagingContent && doc.text.length > 0) {
      // XXX: Test this again.
      // this.setState({ isStagingContent: true });
      this.setState({ dockDisplayContext: DOCK.stageContent });
    }
  }

  onChange = (change) => {
    const { value } = change
    this.setState({ blogValue: value })
  }

  openDock = (dockDisplayContext) => {
    // If dock is currently open, first close then open it
    // in sequential transition.
    if (!!this.state.dockDisplayContext) {
      const showDockWithTimeout = () => (
        setTimeout(
          () => this.setState({ dockDisplayContext }),
          DOCK_TRANSITION_DURATION
        )
      );

      return this.setState({ dockDisplayContext: null }, showDockWithTimeout);
    }

    this.setState({ dockDisplayContext });
  }

  toggleDock = (dockDisplayContext) => {
    if (this.state.dockDisplayContext === dockDisplayContext) {
      return this.closeDock();
    }

    if (dockDisplayContext) {
      return this.openDock(dockDisplayContext);
    }
  }

  closeDock = () => {
    // TODO: Need to save config state in StageContentPanel?
    this.setState({ dockDisplayContext: null });
  }

  stageContent = () => {
    this.openDock(DOCK.stageContent);
  }

  showUserGuide = () => {
    this.openDock(DOCK.userGuide);
  }

  renderOverrideNode = (props) => {
    props = { ...props, renderMode: RM.RENDER_FOR_EDIT };

    switch (props.node.type) {
      case NT.headline:
        const { document: doc } = props.editor.value;
        const hasToolbarRow = doc.nodes.size === 1;

        return (
          <HeadlineSwitch
            {...props}
            hasToolbarRow={hasToolbarRow}
          />
        );
      case NT.image:
        // With renderMode of for edit.
        return renderDefaultNode(props);
      default: return null;
    }
  }

  decorateNode = (node) => {
    let decos = [];

    switch (node.type) {
      case NT.headline:
        const text = node.getTexts().first();
        const headlineLength = node.text.length;

        // Check for trailing spaces and make them appear
        // '&nbsp;' x count for formating.
        // TODO: Maybe also do it for multiple spaces in the middle.
        const match = node.text.match(/ {2,}$/);
        if (match) {
          const [ trailingSpace ] = match;
          const targetLength = trailingSpace.length - 1;
          const nbspRange = {
            anchorKey: text.key,
            anchorOffset: headlineLength - targetLength,
            focusKey: text.key,
            focusOffset: headlineLength,
            marks: [{
              type: MT.nbsp,
              data: { count: targetLength },
            }],
          };
          decos = [...decos, nbspRange];
        }

        if (headlineLength > HEADLINE_CHAR_LIMIT) {
          const hiRange = {
            anchorKey: text.key,
            anchorOffset: HEADLINE_CHAR_LIMIT,
            focusKey: text.key,
            focusOffset: headlineLength,
            marks: [{
              type: MT.highlighted,
            }],
          };
          decos = [...decos, hiRange];
        }
        break;

      default:
        return;
    }

    return decos;
  }

  renderNavbarPortal = () => {
    const { document: doc } = this.state.blogValue;
    const headline = doc.nodes.first()
    const headlineIsEmpty = headline.text.trim().length === 0;
    const headlineIsOverLimit = headline.text.length > HEADLINE_CHAR_LIMIT;

    const navbarPortalRoot = document.getElementById(NAVBAR_PORTAL_ROOT_ID);

    // Account for horizontal padding in navbar.
    const controlGroupConWidth = SIDE_DOCK_WIDTH - 64;

    return (
      <Portal node={navbarPortalRoot}>
        <div
          className="absolute top-0 left-0 right-0 h-100 ph5"
          style={{
            background: BASE_COLOR.white,
            opacity: 0.9,
          }}
        >
          <div className="h-100 flex items-center">
            <div className="w-100 flex justify-end">
              <div
                className="pl3 pr4 pr0-l tr tl-l"
                style={{ width: controlGroupConWidth }}
              >
                <StageContentBtn
                  disabled={headlineIsEmpty || headlineIsOverLimit}
                  onClick={this.stageContent}
                />
              </div>
            </div>
          </div>
        </div>
      </Portal>
    );
  }

  renderDockInner = (dockProps) => {
    const { size } = dockProps;

    switch (this.state.dockDisplayContext) {
      case DOCK.stageContent:
        const { document: doc } = this.state.blogValue;
        const hasValidContent = doc.text.length > 0;

        return (
          <StageContentPanel
            style={{ minHeight: '100vh' }}
            renderedWidth={size}
            onCancel={this.closeDock}
            hasValidContent={hasValidContent}
            renderExportPreview={this.renderExportPreview}
          />
        );

      case DOCK.userGuide:
        return (
          <BlogUserGuidePanel
            onCancel={this.closeDock}
          />
        );

      default:
        return null;
    }
  }

  renderDockPortal = () => {
    // If viewport is wide enough for dock to be open and
    // not obstruct the editor, let it stay open.
    const w = viewportWidth();
    const isWideEnough = (w - COMPOSER_INNER_MAX_WIDTH) / 2 > SIDE_DOCK_WIDTH;
    const dimMode = isWideEnough ? 'none' : 'opaque';

    const isVisible = !!this.state.dockDisplayContext;
    return (
      <Portal>
        <Dock
          isVisible={isVisible}
          position="right"
          dimMode={dimMode}
          fluid={false}
          zIndex={10}
          onVisibleChange={this.closeDock}
          duration={DOCK_TRANSITION_DURATION}
          size={SIDE_DOCK_WIDTH}
        >
          {dockProps => this.renderDockInner(dockProps)}
        </Dock>
      </Portal>
    );
  }

  renderPlaceholder = (props) => {
    const { node } = props;

    switch (node.type) {
      case NT.headline:
        if (node.text !== '') return;
        return (
          <Placeholder
            style={{ color: BASE_COLOR.moonLightGray }}
            children="Write one true sentence.."
          />
        );

      default:
        return;
    }
  }

  renderExportPreview = (previewProps = {}) => {
    const {
      rootId,
      className,
      style = {},
      onClick = noop,
    } = previewProps;

    const { document: doc } = this.state.blogValue;
    const nonEmptyBlocks = doc.nodes.filter(n => !n.isEmpty);

    const previewCls = (nonEmptyBlocks.size > 1
      ? exportPreviewClass.long
      : exportPreviewClass.short
    );

    return (
      <div className={className} style={style} onClick={onClick}>
        <BlogExportPreview
          rootId={rootId}
          innerClass={previewCls}
          value={this.state.blogValue}
        />
      </div>
    );
  }

  renderBRCFramePortal = () => {
    return (
      <Portal>
        <BottomRightCornerFrame className={cx(
          "mb3 pr2 pr3-ns",
          cn.animateFadeInSlow,
        )}>
          <Popover placement="top">
            <HelpMenuBtn />

            {popWithMotion(
              // TODO: Probably extract below out.
              <Menu>
                <Menu.Row href="mailto:hello@moagraph.com">
                  Feedback
                </Menu.Row>
                <Menu.Row onClick={this.showUserGuide}>
                  <div className="w-100 flex justify-between">
                    <div>Keyboard shortcuts</div>
                    <div
                      className="f7 moon-gray"
                      style={{ paddingTop: '3px' }}
                    >
                      <kbd>⌘</kbd>
                      <span className="ph1">+</span>
                      <kbd>/</kbd>
                    </div>
                  </div>
                </Menu.Row>
              </Menu>
            )}
          </Popover>
        </BottomRightCornerFrame>
      </Portal>
    );
  }

  handleKeydown = (e) => {
    const isDockOpen = !!this.state.dockDisplayContext;
    const { code, kc } = keycodeFromEvent(e);

    const isEscape              = code === kc.codes.esc;
    const isStageContentToggle  = e.metaKey && e.shiftKey && code === kc.codes['p'];
    const isUserGuideToggle     = e.metaKey && code === kc.codes['/'];

    if (isDockOpen && isEscape) {
      return this.closeDock();
    }

    if (isStageContentToggle) {
      return this.toggleDock(DOCK.stageContent);
    }

    if (isUserGuideToggle) {
      return this.toggleDock(DOCK.userGuide);
    }
  }

  render() {
    return (
      <div
        className="pv6 pv7-l"
        style={{ animation: 'opacityFadeInFull 500ms, translatePositionUp 350ms' }}
      >
        <div
          className="center ph0 ph3-l"
          style={{ maxWidth: `${COMPOSER_INNER_MAX_WIDTH}px` }}
        >
          <MediumRareEditor
            autoFocus={true}
            className="ph3"
            schema={schema}
            value={this.state.blogValue}
            onChange={this.onChange}
            onKeyDown={this.debounceKeepDraftState}
            renderNode={this.renderOverrideNode}
            decorateNode={this.decorateNode}
            renderPlaceholder={this.renderPlaceholder}
            firstPlugins={[
              ...overrideSlatePlugins,
            ]}
            lastPlugins={[
              ...autoReplacePlugins,
              ...hotkeyPlugins,
              TrimDocumentOnBlur({ onlyFrom: [NT.headline] }),
            ]}
          />
        </div>

        {this.renderBRCFramePortal()}
        {this.renderNavbarPortal()}
        {this.renderDockPortal()}

        <EventListener type="keydown" handler={this.handleKeydown} />
      </div>
    );
  }
}

BlogComposer.moduleId = formatModuleId({
  moduleName: 'note',
  platform: 'web',
  version: 1,
});


// Using roboto font sometimes causes preview export to
// look differently in saved file than preview; e.g. when
// line is almost full till the end.
const previewHeadlineFont = 'arial, sans-serif';

const slateEditor = '[data-slate-editor="true"]';
const exportPreviewClass = {
  short: css({
    [`& ${slateEditor} > h1`]: {
      fontFamily: previewHeadlineFont,
      fontSize: '20px',
      lineHeight: 1.5,
    },
  }),

  long: css({
    // All
    [`& ${slateEditor}`]: {
      fontSize: '16px',
      lineHeight: 1.375,
    },

    // All non-headline top level blocks
    [`& ${slateEditor} > *`]: {
      margin: '0 auto 0.75rem',
    },

    // Headline
    [`& ${slateEditor} > h1`]: {
      fontFamily: previewHeadlineFont,
      fontSize: '18px',
      lineHeight: 1.375,
    },

    // Heading
    [`& ${slateEditor} > h2`]: {
      fontFamily: previewHeadlineFont,
      // Because we are using arial for sans font instead of roboto,
      // and it doesn't have font weight 500.
      fontWeight: 600,
      fontSize: '14px !important',
      margin: '1.25rem auto 0.25rem',
    },

    // list
    [`& ${slateEditor} ul`]: { paddingLeft: 'calc(1rem + 3px)' },
    [`& ${slateEditor} ol`]: { paddingLeft: 'calc(1rem + 3px)' },
    [`& ${slateEditor} li`]: { margin: '1px auto' },
    [`& ${slateEditor} li p`]: { margin: 0 },

    // code block
    [`& ${slateEditor} > pre`]: {
      fontSize: '9px !important',
      overflow: 'hidden',
    },
    [`& ${slateEditor} > pre > code`]: {
      padding: '.5rem !important'  // pa2
    },

    // Blockquote
    [`& ${slateEditor} > blockquote`]: {
      fontSize: '14px',
      padding: '0 0.75rem',
    },

    // code mark
    [`& ${slateEditor} > *:not(pre) code`]: {
      fontSize: '12px',
    },
    [`& ${slateEditor} > blockquote code`]: {
      fontSize: '10px',
    },
  }),
};


export default compose(
  withRouter,
  connect(null, { keepDraftState }),
)(BlogComposer);



// WEBPACK FOOTER //
// ./src/components/modules/blog/BlogComposer.js